<template>
  <div>
    <section
      :class="{ newComment: newComment, active: isEditable }"
      class="user-comment--container"
    >
      <asc-user-details
        :full-name="getFullName"
        :is-user="isUser"
        :new-comment="newComment"
        :last-updated="getLastUpdatedTimeStamp"
        :disabled="disabled"
        :can-edit="isEditable"
        :is-expanded="isExpanded"
        @onDeleteComment="handleDeleteComment"
        @onEditComment="handleEditComment"
      />
      <asc-rich-text-input
        class="user-comment--input"
        :class="{ newComment }"
        :can-edit="isEditable"
        :comment="comment"
        :new-comment="newComment"
        :user-list="userList"
        :clear="clear"
        :can-post="!!canPost"
        :placeholder="editorPlaceHolder"
        :disabled="disabled"
        :comment-change-event-on-blur="commentChangeEventOnBlur"
        hide-details
        hidden-menu
        allow-images
        @updated="handleUpdatedText"
        @disableEdit="disableEdit"
      />
    </section>

    <div class="user-comment--footer">
      <asc-label
        v-if="canEdit && canPost && helperText.length"
        class="label"
        size="sm"
        :value="helperText"
        :color="getFontColor"
      />
      <asc-accent-button
        v-if="hasPostBtn"
        button-text="POST"
        :on-click="handlePost"
        :outline="true"
        :secondary="true"
        :disabled="!canPost"
      />
    </div>
  </div>
</template>

<script>
import AscLabel from '@/components/atoms/AscLabel'
import AscAccentButton from '@/components/atoms/AscAccentButton'
import AscRichTextInput from '@/components/atoms/AscRichTextInput'
import { AscUserCommentPropsMixin } from '@/components/mixins'
import variables from '@/styles/_varsJs.scss'
import { getCurrentUTCTimeStamp } from '@/utilities/dates'
import AscUserDetails from './AscUserDetails'

export default {
  name: 'AscUserComment',

  components: {
    AscAccentButton,
    AscRichTextInput,
    AscUserDetails,
    AscLabel,
  },

  mixins: [AscUserCommentPropsMixin],

  props: {
    isExpanded: {
      type: Boolean,
      default: false,
    },
    newComment: {
      type: Boolean,
      default: () => false,
    },
    userComment: {
      type: Object,
      default: () => {},
    },
    resetComment: {
      type: Boolean,
      default: false,
    },
    editorPlaceHolder: {
      type: String,
      default: 'Add a comment.',
    },
    helperText: {
      type: String,
      default: '',
    },
    hasPostBtn: {
      type: Boolean,
      default: false,
    },
    commentChangeEventOnBlur: {
      type: Boolean,
      default: true,
    },
  },

  data() {
    return {
      canPost: false,
      canEdit: false,
      comment: this.userComment ? this.userComment.comment : '',
      disablePost: true,
      clear: false,
      hasData: false,
    }
  },

  computed: {
    isEditable() {
      return (
        (this.newComment && !this.disabled) ||
        (this.canEdit && !this.newComment)
      )
    },
    getFullName() {
      return this.userComment && !this.newComment
        ? `${this.userComment?.firstName} ${this.userComment?.lastName}`
        : `${this.user.firstName} ${this.user.lastName}`
    },
    getLastUpdatedTimeStamp() {
      if (this.newComment) {
        return ''
      } else {
        return this.userComment.updatedOn
          ? this.userComment.updatedOn
          : this.userComment.createdOn
      }
    },
    getFontColor() {
      return variables.gray600
    },
  },

  watch: {
    comment(newValue) {
      this.comment = newValue
      this.canEdit =
        this.newComment && this.comment !== '' ? true : this.canEdit
      this.disablePost = false
    },
    resetComment(newValue) {
      this.clear = newValue
    },
  },

  methods: {
    disableEdit() {
      this.canEdit = false
    },
    handleDeleteComment() {
      this.$emit('delete', this.userComment.id)
    },
    handleEditComment() {
      this.canEdit = true
    },
    handleUpdatedText(text) {
      this.canEdit = false
      const parser = new DOMParser().parseFromString(text, 'text/html')
      const textContent = parser.documentElement.textContent
      this.canPost =
        (textContent && textContent.trim().length) || parser.images.length
      this.comment = text

      this.newComment && this.comment !== '<p></p>'
        ? (this.hasData = true)
        : this.comment == '<p></p>'
        ? (this.hasData = false)
        : this.hasData

      if (this.comment == '') {
        this.hasData = false
      }

      this.$emit('hasData', this.hasData)

      const userInfo = this.newComment ? this.user : this.userComment
      const usersTagged = this.parseComment(this.comment)
      const newPost = {
        firstName: userInfo.firstName,
        lastName: userInfo.lastName,
        comment: this.comment,
        isNew: this.newComment,
        isUpdated: !this.newComment,
        isDeleted: userInfo.isDeleted ? userInfo.isDeleted : null,
        createdOn:
          !this.newComment && this.userComment
            ? this.userComment.createdOn
            : null,
        updatedOn:
          !this.newComment && this.userComment
            ? this.userComment.updatedOn
            : null,
        userId: userInfo.userId,
        id: this.userComment ? this.userComment.id : getCurrentUTCTimeStamp(),
        usersTagged: usersTagged,
      }
      this.$emit('commentText', newPost)

      if (!this.hasPostBtn) {
        if (this.canPost) {
          this.$emit('post', newPost)
        } else {
          this.$emit('clearOutComment', newPost)
        }
      }
    },

    handlePost() {
      const userInfo = this.newComment ? this.user : this.userComment
      const usersTagged = this.parseComment(this.comment)

      const newPost = {
        firstName: userInfo.firstName,
        lastName: userInfo.lastName,
        comment: this.comment,
        isNew: this.newComment,
        isUpdated: !this.newComment,
        isDeleted: userInfo.isDeleted ? userInfo.isDeleted : null,
        createdOn:
          !this.newComment && this.userComment
            ? this.userComment.createdOn
            : null,
        updatedOn:
          !this.newComment && this.userComment
            ? this.userComment.updatedOn
            : null,
        userId: userInfo.userId,
        id: this.userComment ? this.userComment.id : getCurrentUTCTimeStamp(),
        usersTagged: usersTagged,
      }
      if (this.canPost && this.hasPostBtn) {
        this.hasData = false
        this.comment = '<p></p>'
        this.$emit('hasData', this.hasData)
        this.$emit('onPostClick', newPost)
      }
    },
    // parse the HTML of the comment since the mentions are well formed span tags.
    // this ensures we capture if a user tag is deleted, added, etc.
    parseComment(comment) {
      const parser = new DOMParser()
      const htmlDOC = parser.parseFromString(comment, 'text/html')
      return Array.from(htmlDOC.getElementsByClassName('mention')).map(
        userMention => {
          return Number(userMention.attributes['data-mention-id'].value)
        },
      )
    },
  },
}
</script>

<style lang="scss">
.user-comment--input {
  margin-left: 42px !important;

  &.newComment {
    width: 100%;
  }
}

.user-comment {
  &--footer {
    display: flex;
    flex-flow: row nowrap;
    justify-content: flex-end;

    .label {
      margin-top: 8px;
      padding: 5px;
    }

    .asc-accent-button-container {
      margin-top: 8px;

      .v-btn {
        background-color: $accent-100 !important;
        border: none;
      }
    }
  }

  &--container.newComment {
    flex-flow: row nowrap;
    justify-content: flex-start;
    align-items: flex-start;
  }

  &--container.newComment &--input {
    margin-left: 0 !important;
  }

  &--container {
    display: flex;
    flex-flow: column nowrap;

    & .user-detail--container > .user-detail--menu {
      opacity: 0;
    }

    &:hover {
      & .user-detail--container > .user-detail--menu {
        opacity: 1;
      }
    }
  }
}
</style>
